import type { Area } from 'react-easy-crop';

// Returns integer [p, q] parts so canvas dimensions are always exactly p*k × q*k.
export function parseAspectParts(value?: string): [number, number] | undefined {
  if (!value) return undefined;
  const parts = value.split(':').map(Number);
  if (
    parts.length === 2 &&
    Number.isInteger(parts[0]) &&
    Number.isInteger(parts[1]) &&
    parts[0] > 0 &&
    parts[1] > 0
  ) {
    return [parts[0], parts[1]];
  }
  return undefined;
}

// Snaps the output to exact integer multiples of the aspect parts so the file
// is always precisely p*k × q*k. k is clamped to 1 so a crop narrower than one
// aspect unit can never produce a 0×0 canvas (which would silently yield a
// corrupt file instead of an error).
export function computeCanvasSize(
  pixelCrop: Pick<Area, 'width' | 'height'>,
  aspectParts?: [number, number]
): { width: number; height: number } {
  if (!aspectParts) {
    return { width: Math.round(pixelCrop.width), height: Math.round(pixelCrop.height) };
  }
  const [p, q] = aspectParts;
  const k = Math.max(1, Math.round(pixelCrop.width / p));
  return { width: k * p, height: k * q };
}

export async function getCroppedImg(
  imageSrc: string,
  pixelCrop: Area,
  filename: string,
  aspectParts?: [number, number]
): Promise<File> {
  const image = new Image();
  image.src = imageSrc;
  await new Promise<void>((resolve, reject) => {
    image.onload = () => resolve();
    image.onerror = () => reject(new Error('Failed to load image'));
  });

  const { width: canvasWidth, height: canvasHeight } = computeCanvasSize(pixelCrop, aspectParts);

  const canvas = document.createElement('canvas');
  canvas.width = canvasWidth;
  canvas.height = canvasHeight;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Failed to get canvas context');

  ctx.drawImage(
    image,
    pixelCrop.x,
    pixelCrop.y,
    pixelCrop.width,
    pixelCrop.height,
    0,
    0,
    canvasWidth,
    canvasHeight
  );

  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (!blob) {
        reject(new Error('Canvas is empty'));
        return;
      }
      resolve(new File([blob], filename, { type: 'image/jpeg' }));
    }, 'image/jpeg');
  });
}
